import { useEffect, useId, useRef, useState } from 'react';
import type { BusinessProfileDraft } from '@mouja/domain/analysis';
import { parseIdeaEntryInput } from '@mouja/domain/idea-entry';
import { useLanguage } from '../../i18n/language.js';
import { Button, Field } from '../../merchant/ui/primitives.js';
import { missingProfileConfirmationFields } from '../../merchant/profileCompleteness.js';
import { ApiFailure, newIdempotencyKey } from '../../workspace/api.js';
import {
  startIdeaJourney,
  type IdeaStartInput,
  type PendingIdeaStart,
} from './firstIdeaApi.js';
import { firstIdeaCopy } from './firstIdeaCopy.js';
import './first-idea.css';

export function IdeaJourneyMap({
  step,
  independent = false,
}: {
  step: 0 | 1 | 2;
  independent?: boolean;
}) {
  const { language } = useLanguage(),
    c = firstIdeaCopy[language];
  return (
    <ol
      className="mj-first-map"
      aria-label={language === 'en' ? 'Your next steps' : 'خطواتك القادمة'}
    >
      {[independent ? c.yourTopic : c.business, c.idea, c.script].map(
        (label, index) => (
          <li key={label} aria-current={index === step ? 'step' : undefined}>
            <span aria-hidden="true">{index < step ? '✓' : index + 1}</span>
            {label}
          </li>
        ),
      )}
    </ol>
  );
}
type Draft = {
  independent: boolean;
  topic: string;
  audience: string;
  own: boolean;
  seed: string;
  focus: string;
  instructions: string;
  inactiveContextDraft: { seed: string; instructions: string };
  locale: 'en' | 'ar';
  pending: { key: string; body: IdeaStartInput } | null;
};
function load(key: string, locale: 'en' | 'ar'): Draft {
  try {
    const raw = JSON.parse(
      sessionStorage.getItem(key) ?? 'null',
    ) as Partial<Draft> | null;
    if (
      raw &&
      typeof raw.own === 'boolean' &&
      typeof raw.seed === 'string' &&
      typeof raw.focus === 'string' &&
      typeof raw.instructions === 'string' &&
      (raw.locale === 'en' || raw.locale === 'ar')
    ) {
      const context = {
        independent: raw.independent === true,
        topic: typeof raw.topic === 'string' ? raw.topic : '',
        audience: typeof raw.audience === 'string' ? raw.audience : '',
        inactiveContextDraft:
          typeof raw.inactiveContextDraft?.seed === 'string' &&
          typeof raw.inactiveContextDraft.instructions === 'string'
            ? raw.inactiveContextDraft
            : { seed: '', instructions: '' },
      };
      const pending = raw.pending;
      if (pending && typeof pending.key === 'string') {
        parseIdeaEntryInput(pending.body.entry);
        return { ...raw, ...context, pending } as Draft;
      }
      return { ...raw, ...context, pending: null } as Draft;
    }
  } catch {
    /* Unavailable storage does not block entry. */
  }
  return {
    independent: false,
    topic: '',
    audience: '',
    own: false,
    seed: '',
    focus: '',
    instructions: '',
    inactiveContextDraft: { seed: '', instructions: '' },
    locale,
    pending: null,
  };
}
export function FirstIdeaEntry({
  profile,
  sourceIntakeId,
  expectedProfileArtifactId,
  firstStart,
  onStarted,
  onEdit,
  onCancel,
  pendingStart,
  embedded = false,
}: {
  profile: BusinessProfileDraft;
  sourceIntakeId: string;
  expectedProfileArtifactId: string;
  firstStart: boolean;
  onStarted: (lineId: string, journeyId: string) => void;
  onEdit?: () => void;
  onCancel?: () => void;
  pendingStart?: PendingIdeaStart | null;
  embedded?: boolean;
}) {
  const { language } = useLanguage(),
    c = firstIdeaCopy[language],
    id = useId();
  const modeTabs = useRef<(HTMLButtonElement | null)[]>([]);
  const contextToggle = useRef<HTMLButtonElement>(null);
  const [contextOpen, setContextOpen] = useState(false);
  const storageKey = `mouja:first-idea:${sourceIntakeId}:${String(firstStart)}`;
  const [draft, setDraft] = useState(() => load(storageKey, language));
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<string | null>(null);
  const missing = missingProfileConfirmationFields(profile);
  useEffect(() => {
    if (!pendingStart) return;
    const entry = pendingStart.body.entry;
    setDraft({
      independent: entry.context !== undefined,
      topic: entry.context?.topic ?? '',
      audience: entry.context?.audience ?? '',
      own: entry.mode === 'DEVELOP_MY_IDEA',
      seed: entry.originalSeed ?? '',
      focus: entry.optionalFocus ?? '',
      instructions: entry.instructions ?? '',
      inactiveContextDraft: { seed: '', instructions: '' },
      locale: entry.outputLocale,
      pending: pendingStart,
    });
  }, [pendingStart]);
  useEffect(() => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(draft));
    } catch {
      /* Private browsing may deny storage. */
    }
  }, [draft, storageKey]);
  const patch = (change: Partial<Draft>) => {
    setDraft((d) => ({ ...d, ...change }));
    setError(null);
  };
  async function submit() {
    if (busy || missing.length > 0) return;
    let attempt = draft.pending;
    try {
      if (attempt === null) {
        const entry = parseIdeaEntryInput({
          mode: draft.own ? 'DEVELOP_MY_IDEA' : 'DISCOVER_FOR_ME',
          originalSeed: draft.own ? draft.seed : null,
          optionalFocus: draft.own || draft.independent ? null : draft.focus,
          instructions: draft.instructions,
          outputLocale: draft.locale,
          ...(draft.independent
            ? {
                context: {
                  kind: 'INDEPENDENT_TOPIC',
                  topic: draft.own ? draft.seed : draft.topic,
                  audience: draft.audience,
                },
              }
            : {}),
        });
        attempt = {
          key: newIdempotencyKey(),
          body: { sourceIntakeId, expectedProfileArtifactId, firstStart, entry },
        };
        patch({ pending: attempt });
        try {
          sessionStorage.setItem(
            storageKey,
            JSON.stringify({ ...draft, pending: attempt }),
          );
        } catch {
          /* Request key remains in memory. */
        }
      }
    } catch {
      setError(c.invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await startIdeaJourney(attempt.body, attempt.key);
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* No durable browser requirement. */
      }
      onStarted(result.productionLineId, result.journeyId);
    } catch (failure) {
      // A definite validation refusal made no approval: allow correction. An
      // uncertain response keeps the original body/key for safe replay.
      if (failure instanceof ApiFailure && failure.kind === 'invalid') {
        setDraft((current) => ({ ...current, pending: null }));
        setError(c.invalid);
      } else setError(c.startFailed);
    } finally {
      setBusy(false);
    }
  }
  const locked = busy || draft.pending !== null;
  return (
    <section className="mj-first mj-first-entry" data-idea-journey="v1">
      <IdeaJourneyMap step={0} independent={draft.independent} />
      {!embedded && <h1>{firstStart ? c.confirm : c.returning}</h1>}
      <div
        className="mj-first-context-picker"
        onKeyDown={(event) => {
          if (contextOpen && event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            setContextOpen(false);
            contextToggle.current?.focus();
          }
        }}
      >
        <div className="mj-first-context-summary">
          <span>
            {c.forLabel}{' '}
            <bdi>{draft.independent ? c.different : profile.identity.value}</bdi>
          </span>
          <span aria-hidden="true">·</span>
          <button
            ref={contextToggle}
            type="button"
            className="mj-first-context-toggle"
            aria-expanded={contextOpen}
            aria-controls={`${id}-context-options`}
            disabled={locked}
            onClick={() => {
              setContextOpen((open) => !open);
            }}
          >
            {c.change}
          </button>
        </div>
        <div
          id={`${id}-context-options`}
          className="mj-first-context-choice"
          role="group"
          aria-label={c.contextChoice}
          hidden={!contextOpen}
        >
          {[c.forBusiness, c.different].map((label, index) => (
            <button
              key={label}
              type="button"
              aria-pressed={draft.independent === (index === 1)}
              disabled={locked}
              onClick={() => {
                if (draft.independent !== (index === 1)) {
                  patch({
                    independent: index === 1,
                    seed: draft.inactiveContextDraft.seed,
                    instructions: draft.inactiveContextDraft.instructions,
                    inactiveContextDraft: {
                      seed: draft.seed,
                      instructions: draft.instructions,
                    },
                  });
                }
                setContextOpen(false);
                contextToggle.current?.focus();
              }}
            >
              <span aria-hidden="true" className="mj-first-context-check">
                {draft.independent === (index === 1) ? '✓' : ''}
              </span>
              {label}
            </button>
          ))}
        </div>
      </div>
      {firstStart && (
        <div className="mj-first-business">
          <h2 dir="auto">{profile.identity.value}</h2>
          {profile.niche.value && <p dir="auto">{profile.niche.value}</p>}
          <details>
            <summary>{c.details}</summary>
            <ul>
              {[
                profile.geography.value,
                ...profile.offers.map((offer) => offer.title.value),
              ]
                .filter(Boolean)
                .map((value, index) => (
                  <li key={index} dir="auto">
                    {value}
                  </li>
                ))}
            </ul>
            {onEdit && (
              <Button onClick={onEdit} disabled={locked}>
                {c.edit}
              </Button>
            )}
          </details>
        </div>
      )}
      {draft.independent && <p className="mj-first-context-note">{c.separate}</p>}
      {missing.length > 0 && (
        <p role="alert">
          {c.missing} {onEdit && <Button onClick={onEdit}>{c.edit}</Button>}
        </p>
      )}
      <div className="mj-first-entry-controls">
        <div className="mj-first-mode-tabs" role="tablist" aria-label={c.startWith}>
          {[c.find, c.own].map((label, index) => (
            <button
              key={index}
              ref={(node) => {
                modeTabs.current[index] = node;
              }}
              id={`${id}-mode-${index}`}
              type="button"
              role="tab"
              aria-selected={Number(draft.own) === index}
              aria-controls={`${id}-mode-panel`}
              tabIndex={Number(draft.own) === index ? 0 : -1}
              disabled={locked}
              onClick={() => {
                patch({ own: index === 1 });
              }}
              onKeyDown={(event) => {
                if (
                  locked ||
                  !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
                )
                  return;
                event.preventDefault();
                // With two adjoining tabs either arrow reaches the other tab in LTR and RTL.
                const next =
                  event.key === 'Home' ? 0 : event.key === 'End' ? 1 : 1 - index;
                patch({ own: next === 1 });
                modeTabs.current[next]?.focus();
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          className="mj-first-mode-panel"
          role="tabpanel"
          id={`${id}-mode-panel`}
          aria-labelledby={`${id}-mode-${Number(draft.own)}`}
          tabIndex={0}
        >
          {draft.independent && !draft.own && (
            <Field id={`${id}-topic`} label={c.topic}>
              <textarea
                id={`${id}-topic`}
                className="mj-ws-inp"
                rows={2}
                dir="auto"
                maxLength={2000}
                disabled={locked}
                value={draft.topic}
                onChange={(e) => {
                  patch({ topic: e.target.value });
                }}
              />
            </Field>
          )}
          {draft.own && (
            <Field id={`${id}-seed`} label={c.seed} hint={c.seedHint}>
              <textarea
                id={`${id}-seed`}
                className="mj-ws-inp"
                rows={3}
                dir="auto"
                maxLength={2000}
                disabled={locked}
                value={draft.seed}
                onChange={(e) => {
                  patch({ seed: e.target.value });
                }}
              />
            </Field>
          )}
          {draft.independent && (
            <Field id={`${id}-audience`} label={c.audience}>
              <input
                id={`${id}-audience`}
                className="mj-ws-inp"
                dir="auto"
                maxLength={500}
                disabled={locked}
                value={draft.audience}
                onChange={(e) => {
                  patch({ audience: e.target.value });
                }}
              />
            </Field>
          )}
          <Field id={`${id}-language`} label={c.language}>
            <select
              id={`${id}-language`}
              className="mj-ws-inp"
              value={draft.locale}
              disabled={locked}
              onChange={(e) => {
                patch({ locale: e.target.value === 'ar' ? 'ar' : 'en' });
              }}
            >
              <option value="en">English</option>
              <option value="ar">العربية</option>
            </select>
          </Field>
          <details>
            <summary>
              {c.more}
              {draft.instructions.trim() ? ` · ${c.added}` : ''}
            </summary>
            {!draft.own && !draft.independent && (
              <Field id={`${id}-focus`} label={c.focus}>
                <input
                  id={`${id}-focus`}
                  className="mj-ws-inp"
                  dir="auto"
                  maxLength={2000}
                  disabled={locked}
                  value={draft.focus}
                  onChange={(e) => {
                    patch({ focus: e.target.value });
                  }}
                />
              </Field>
            )}
            <Field id={`${id}-instructions`} label={c.instructions}>
              <textarea
                id={`${id}-instructions`}
                className="mj-ws-inp"
                dir="auto"
                maxLength={4000}
                rows={3}
                disabled={locked}
                value={draft.instructions}
                onChange={(e) => {
                  patch({ instructions: e.target.value });
                }}
              />
            </Field>
          </details>
        </div>
      </div>
      {error && <p role="alert">{error}</p>}
      <div className="mj-first-action mj-first-entry-action">
        <div className="mj-first-action-row">
          {onCancel && (
            <Button variant="quiet" onClick={onCancel} disabled={busy}>
              {c.cancel}
            </Button>
          )}
          <Button
            variant="primary"
            pixelWave
            disabled={
              busy ||
              missing.length > 0 ||
              (draft.own && !draft.seed.trim()) ||
              (draft.independent && !draft.own && !draft.topic.trim())
            }
            aria-busy={busy}
            onClick={() => void submit()}
          >
            {busy
              ? c.starting
              : draft.pending
                ? c.retry
                : firstStart
                  ? draft.own
                    ? c.confirmDevelop
                    : c.confirmFind
                  : draft.own
                    ? c.develop
                    : c.find}
          </Button>
        </div>
        <p>{draft.own ? c.nextOwn : c.next}</p>
      </div>
    </section>
  );
}
